import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { getStorageItem, setStorageItem } from 'utils';

import { hints } from './hints';
import SpotlightContext from './SpotlightContext';
import SpotlightOverlay from './SpotlightOverlay';

const SEEN_KEY = '@eskolare/spotlight-seen';

const readSeen = () => {
  const stored = getStorageItem(SEEN_KEY, []);
  return Array.isArray(stored) ? stored : [];
};

const isExpired = (hint, now) => {
  const at = Date.parse(hint.expiresAt);
  if (Number.isNaN(at)) return false;
  return at <= now;
};

const byPriority = (a, b) => (a.priority || 0) - (b.priority || 0);

const getEligibleHints = (seenIds, now) => {
  const seen = new Set(seenIds);
  return hints
    .filter(hint => hint && hint.id)
    .filter(hint => !seen.has(hint.id))
    .filter(hint => !isExpired(hint, now))
    .sort(byPriority);
};

const persistSeen = id => {
  const seen = readSeen();
  if (!seen.includes(id)) setStorageItem(SEEN_KEY, [...seen, id]);
};

export function SpotlightProvider({ children }) {
  const eligibleRef = useRef(null);
  if (eligibleRef.current === null) {
    eligibleRef.current = getEligibleHints(readSeen(), Date.now());
  }

  const [registered, setRegistered] = useState(() => new Map());
  const shownRef = useRef(new Set());
  const [currentId, setCurrentId] = useState(null);

  const register = useCallback(
    (id, element) => setRegistered(prev => new Map(prev).set(id, element)),
    [],
  );

  const unregister = useCallback(
    id =>
      setRegistered(prev => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      }),
    [],
  );

  const isPending = useCallback(
    hint => registered.has(hint.id) && !shownRef.current.has(hint.id),
    [registered],
  );

  useEffect(() => {
    if (currentId) {
      if (!registered.has(currentId)) setCurrentId(null);
      return;
    }
    const next = eligibleRef.current.find(isPending);
    if (!next) return;

    shownRef.current.add(next.id);
    persistSeen(next.id);
    setCurrentId(next.id);
  }, [registered, currentId, isPending]);

  const value = useMemo(() => ({ register, unregister }), [register, unregister]);

  const currentHint = currentId ? eligibleRef.current.find(hint => hint.id === currentId) : null;
  const currentElement = currentId ? registered.get(currentId) : undefined;
  const hasNext = eligibleRef.current.some(hint => hint.id !== currentId && isPending(hint));

  const handleAdvance = useCallback(() => setCurrentId(null), []);

  return (
    <SpotlightContext.Provider value={value}>
      {children}
      {currentHint && currentElement && (
        <SpotlightOverlay
          hint={currentHint}
          element={currentElement}
          hasNext={hasNext}
          onAdvance={handleAdvance}
        />
      )}
    </SpotlightContext.Provider>
  );
}

SpotlightProvider.propTypes = {
  children: PropTypes.node,
};

export default SpotlightProvider;
export { default as useSpotlightTarget } from './useSpotlightTarget';
